import { createFeature, createReducer, on } from '@ngrx/store';

import { Break } from 'app/features/manual-input-registration/models';
import { done, getInitialProgressState, ProgressState } from 'app/shared/models';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';

import {
    activatingBreakDialog,
    applyingBreakDataFromStorage,
    breakCancelled,
    breakEnded,
    breakStarted,
    deactivatingBreakDialog,
} from './break.actions';

export const breakFeatureKey = 'break';

export interface BreakState {
    break: Break | undefined;
    dialogActivated: boolean;
    loadFromStorageProgress: ProgressState;
    dialogContext: { activityNumber: number } | undefined;
}

export const initialState: BreakState = {
    break: undefined,
    dialogActivated: false,
    loadFromStorageProgress: getInitialProgressState(),
    dialogContext: undefined,
};

export const reducer = createReducer(
    initialState,
    on(applyingBreakDataFromStorage, (state, { breakDataFromStorage }) => {
        if (state.loadFromStorageProgress.done) {
            return state;
        }
        const dialogContext = getUpdatedDialogContext({
            currentDialogContext: state.dialogContext,
            breakData: breakDataFromStorage,
            dialogActivated: state.dialogActivated,
        });

        return {
            ...state,
            break: breakDataFromStorage,
            loadFromStorageProgress: done(),
            dialogContext,
        };
    }),
    on(breakStarted, (state, { breakData }) => {
        return {
            ...state,
            break: breakData,
            dialogContext: getUpdatedDialogContext({
                currentDialogContext: state.dialogContext,
                breakData,
                dialogActivated: state.dialogActivated,
            }),
        };
    }),
    on(
        breakCancelled,
        (state): BreakState => ({
            ...state,
            break: undefined,
            dialogContext: undefined,
        }),
    ),
    on(
        breakEnded,
        (state): BreakState => ({
            ...state,
            break: undefined,
            dialogContext: undefined,
        }),
    ),
    on(activatingBreakDialog, (state) => ({
        ...state,
        dialogActivated: true,
        dialogContext: getUpdatedDialogContext({
            currentDialogContext: state.dialogContext,
            breakData: state.break,
            dialogActivated: true,
        }),
    })),
    on(
        deactivatingBreakDialog,
        (state): BreakState => ({
            ...state,
            dialogActivated: false,
            dialogContext: undefined,
        }),
    ),
    on(resetApiConfig, () => ({
        ...initialState,
        loadFromStorageProgress: done(),
    })),
);

export const breakStateFeature = createFeature({
    name: breakFeatureKey,
    reducer,
});

function getUpdatedDialogContext({
    currentDialogContext,
    dialogActivated,
    breakData,
}: {
    currentDialogContext: { activityNumber: number } | undefined;
    dialogActivated: boolean;
    breakData: Break | undefined;
}): { activityNumber: number } | undefined {
    if (!dialogActivated || !breakData) {
        return undefined;
    }

    if (currentDialogContext?.activityNumber === breakData.activityNumber) return currentDialogContext;

    return { activityNumber: breakData.activityNumber };
}
